import React from "react";
import Link from "next/link";
import { ImageDataType } from "../types";
import { CommonComponentProps } from "./componentTypes";
import Image from "next/image";
import { H3 } from "./H3";
import { MarkdownRenderer } from "./MarkdownRenderer";
import { cn } from "@/lib/utils";

export interface CardCommonInterface {
  headline: string;
  description?: string;
  image: ImageDataType | null;
  tags?: string[];
  href?: string;
  /** Optional label before headline (e.g. 01). Hidden when empty. */
  number?: string | null;
}

interface CardProps extends CommonComponentProps, CardCommonInterface {
  className?: string;
}

export function Card({
  headline,
  description,
  image,
  tags,
  href,
  className,
}: CardProps) {
  const content = (
    <div
      className={cn("flex flex-col justify-start items-start gap-6", className)}
    >
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
        {image && (
          <Image
            className="w-full h-full object-cover"
            src={image.src}
            alt={image.alt}
            fill
          />
        )}
      </div>
      {tags && tags.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {tags.map((tag, tagIndex) => (
            <div
              key={tagIndex}
              className="rounded-pill border border-border bg-background px-4 py-2"
            >
              <div className="text-primary text-xs font-normal">
                {tag}
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="self-stretch flex flex-col justify-start items-start gap-2">
        <H3 className="flex self-stretch justify-start">{headline}</H3>
        {description && (
          <div className="leading-normal [&_.text]:leading-normal">
            <MarkdownRenderer content={description} />
          </div>
        )}
      </div>
    </div>
  );

  if (href) {
    return (
      <Link href={href} className="block">
        {content}
      </Link>
    );
  }

  return content;
}
